import classNames from "classnames"

import CircleOutlineCheckIcon from "@components/elements/V2/Icons/CircleOutlineCheckIcon"
import CircleOutlineCrossIcon from "@components/elements/V2/Icons/CircleOutlineCrossIcon"
import Typography from "@components/elements/V2/Typography"
import { TableRegex } from "@constants/tables"
import { Color } from "@constants/V2/color"
import { getBackgroundColorClass, stringToColor } from "@utils/V2/color"
import React from "react"

const DESKTOP_HIGHLIGHT_COLUMN_INDEX = 1
const MOBILE_HIGHLIGHT_COLUMN_INDEX = 0

const HIGHLIGHT_OPACITY = "bg-opacity-[0.08]"

const LABEL_COLUMN_WIDTH = "w-[160px]"
const ICON_COLUMN_WIDTH = "w-[120px]"

const CELL_CLASSES = "break-words text-left"

const isIconOnlyCell = (cell: string): boolean => {
  const trimmed = cell.trim()

  const checkMatch = trimmed.match(TableRegex.CHECK_ICON)
  if (checkMatch) return checkMatch[0] === trimmed && !checkMatch[2]

  const crossMatch = trimmed.match(TableRegex.CROSS_ICON)
  if (crossMatch) return crossMatch[0] === trimmed && !crossMatch[1]

  return false
}

const renderCellValue = (
  text: string,
  weight: "medium" | "book",
  className?: string,
  centerIcon?: boolean
) => {
  const checkIconMatch = text.match(TableRegex.CHECK_ICON)
  const crossIconMatch = text.match(TableRegex.CROSS_ICON)

  if (checkIconMatch) {
    const [, colorName, label] = checkIconMatch

    return (
      <span
        className={classNames(
          "flex items-center gap-8",
          centerIcon && !label && "w-full justify-center",
          className
        )}
      >
        <CircleOutlineCheckIcon
          overrideColor={stringToColor(colorName) ?? Color.Charcoal}
        />
        {label && (
          <Typography
            size="body-sm"
            font="grotesk"
            weight={weight}
            color={Color.Charcoal}
            text={label}
          />
        )}
      </span>
    )
  }

  if (crossIconMatch) {
    const [, label] = crossIconMatch

    return (
      <span
        className={classNames(
          "flex items-center gap-8",
          centerIcon && !label && "w-full justify-center",
          className
        )}
      >
        <CircleOutlineCrossIcon />
        {label && (
          <Typography
            size="body-sm"
            font="grotesk"
            weight={weight}
            color={Color.Charcoal}
            text={label}
          />
        )}
      </span>
    )
  }

  return (
    <Typography
      size="body-sm"
      font="grotesk"
      weight={weight}
      color={Color.Charcoal}
      text={text}
      className={className}
    />
  )
}

type Props = {
  headers: string[]
  rows: string[][]
  highlightColor: Color
}

const ArticleTable = ({ headers, rows, highlightColor, ...props }: Props) => {
  const highlightClasses = [
    getBackgroundColorClass(highlightColor),
    HIGHLIGHT_OPACITY,
  ]

  const mobileHeaders = headers.slice(1)

  const iconOnlyColumns = headers.map(
    (_, columnIndex) =>
      columnIndex !== 0 && rows.every((row) => isIconOnlyCell(row[columnIndex]))
  )

  const getMobileSidePadding = (columnIndex: number, columnCount: number) => {
    if (columnCount === 1) return "px-20"
    if (columnIndex === 0) return "pl-20 pr-10"
    if (columnIndex === columnCount - 1) return "pl-10 pr-20"
    return "px-10"
  }

  return (
    <div className="w-full" {...props}>
      <div className="-mx-gutter-xs sm:-mx-gutter-sm md:mx-0 lg:hidden">
        <table className="w-full table-fixed border-separate border-spacing-0">
          <thead className="sticky top-[61px] z-20 bg-white md:top-0">
            <tr>
              {mobileHeaders.map((header, columnIndex) => (
                <th
                  key={`mobile-header-${columnIndex}`}
                  scope="col"
                  className={classNames(
                    CELL_CLASSES,
                    "h-60 border-b border-charcoal/50 align-middle",
                    getMobileSidePadding(columnIndex, mobileHeaders.length),
                    columnIndex === MOBILE_HIGHLIGHT_COLUMN_INDEX &&
                      highlightClasses
                  )}
                >
                  <Typography
                    size="body-md-fixed"
                    font="grotesk"
                    weight="medium"
                    color={Color.Charcoal}
                    text={header}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const [label, ...cells] = row

              return (
                <tr key={`mobile-row-${rowIndex}`}>
                  {cells.map((cell, columnIndex) => (
                    <td
                      key={`mobile-cell-${rowIndex}-${columnIndex}`}
                      className={classNames(
                        CELL_CLASSES,
                        "py-12 align-top",
                        getMobileSidePadding(columnIndex, cells.length),
                        "border-b border-charcoal/10",
                        columnIndex === MOBILE_HIGHLIGHT_COLUMN_INDEX &&
                          highlightClasses
                      )}
                    >
                      {renderCellValue(label, "medium", "mb-8")}
                      {renderCellValue(cell, "book", undefined, true)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="hidden overflow-hidden rounded-10 border border-charcoal/20 ring-4 ring-charcoal/5 lg:block">
        <div className="px-20">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr>
                {headers.map((header, columnIndex) => (
                  <th
                    key={`header-${columnIndex}`}
                    scope="col"
                    className={classNames(
                      CELL_CLASSES,
                      "border-b border-charcoal/10 px-10 pb-20 pt-[30px] align-top",
                      columnIndex === DESKTOP_HIGHLIGHT_COLUMN_INDEX &&
                        highlightClasses,
                      {
                        [LABEL_COLUMN_WIDTH]: columnIndex === 0,
                        [ICON_COLUMN_WIDTH]: iconOnlyColumns[columnIndex],
                      }
                    )}
                  >
                    <Typography
                      size="body-md"
                      font="grotesk"
                      weight="medium"
                      color={Color.Charcoal}
                      text={header}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const isLastRow = rowIndex === rows.length - 1

                const getCellClasses = (columnIndex: number) =>
                  classNames(
                    CELL_CLASSES,
                    "px-10 pt-12 align-top",
                    columnIndex === DESKTOP_HIGHLIGHT_COLUMN_INDEX &&
                      highlightClasses,
                    {
                      "border-b border-charcoal/10": !isLastRow,
                      "pb-[22px]": isLastRow,
                      "pb-12": !isLastRow,
                      [LABEL_COLUMN_WIDTH]: columnIndex === 0,
                      [ICON_COLUMN_WIDTH]: iconOnlyColumns[columnIndex],
                    }
                  )

                const renderCellText = (cell: string, isLabel: boolean) =>
                  renderCellValue(cell, isLabel ? "medium" : "book")

                return (
                  <tr key={`row-${rowIndex}`}>
                    {row.map((cell, columnIndex) =>
                      columnIndex === 0 ? (
                        <th
                          key={`cell-${rowIndex}-${columnIndex}`}
                          scope="row"
                          className={getCellClasses(columnIndex)}
                        >
                          {renderCellText(cell, true)}
                        </th>
                      ) : (
                        <td
                          key={`cell-${rowIndex}-${columnIndex}`}
                          className={getCellClasses(columnIndex)}
                        >
                          {renderCellText(cell, false)}
                        </td>
                      )
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ArticleTable
